import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { UploadCloud, Database, RefreshCw, Trash2 } from 'lucide-react';
import { adminApiClient } from '../lib/api';

type DatasetType = 'donors' | 'sales';

const LegacyDataImportPage: React.FC = () => {
  const [datasetType, setDatasetType] = useState<DatasetType>('donors');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState<any[]>([]);
  const [temples, setTemples] = useState<any[]>([]);
  const [selectedTempleId, setSelectedTempleId] = useState('');

  const fetchJobs = async () => {
    try {
      const res = await adminApiClient.getLegacyJobs();
      if (res.success) setJobs(res.data?.jobs || []);
    } catch {
      // ignore
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    const ok = window.confirm('Delete this import job and all its old records? This cannot be undone.');
    if (!ok) return;
    try {
      setLoading(true);
      const res = await adminApiClient.deleteLegacyJob(jobId);
      if (res.success) {
        toast.success('Deleted successfully');
        await fetchJobs();
      } else {
        toast.error(res.message || 'Delete failed');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    (async () => {
      try {
        const res = await adminApiClient.getTemples({ page: 1, limit: 200 });
        if (res.success) {
          setTemples(res.data?.temples || []);
        }
      } catch (e) {
        console.error('Failed to load temples', e);
      }
    })();
  }, []);

  const handleImport = async () => {
    if (!selectedTempleId) {
      toast.error('Please select a temple');
      return;
    }
    if (!file) {
      toast.error('Please choose an Excel file');
      return;
    }
    try {
      setLoading(true);
      const res = await adminApiClient.importLegacyData({
        datasetType,
        templeId: selectedTempleId,
        file,
      });
      if (res.success) {
        toast.success('Imported successfully');
        setFile(null);
        await fetchJobs();
      } else {
        toast.error(res.message || 'Import failed');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Database className="w-6 h-6 text-[var(--color-primary)]" />
            Legacy Data Import
          </h1>
          <p className="text-sm text-gray-600 mt-1">Upload old Excel files into separate collections and optionally merge sales into live donations.</p>
        </div>
        <button
          onClick={fetchJobs}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Target Temple <span className="text-red-500">*</span></label>
            <select
              value={selectedTempleId}
              onChange={(e) => setSelectedTempleId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_30%,white)] focus:border-[var(--color-primary)] outline-none"
            >
              <option value="">Select Temple...</option>
              {temples.map((t) => (
                <option key={t._id || t.id} value={t._id || t.id}>{t.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Data will be mapped to this temple.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dataset Type</label>
            <select
              value={datasetType}
              onChange={(e) => setDatasetType(e.target.value as DatasetType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_30%,white)] focus:border-[var(--color-primary)] outline-none"
            >
              <option value="donors">Donors.xls (Donors)</option>
              <option value="sales">Sales by Donor Detail (Sales)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Choose which file you are uploading.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Excel File</label>
            <input
              type="file"
              accept=".xls,.xlsx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
            />
            <p className="text-xs text-gray-500 mt-1">Supported: .xls, .xlsx</p>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end">
          <button
            onClick={handleImport}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold bg-[linear-gradient(90deg,var(--color-primary),var(--color-tertiary))] hover:opacity-90 disabled:opacity-60"
          >
            <UploadCloud className="w-4 h-4" />
            {loading ? 'Importing…' : 'Import'}
          </button>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Recent Import Jobs</h2>
          <p className="text-sm text-gray-600 mt-1">You can view records and merge sales jobs from the Legacy Data page.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">File</th>
                <th className="text-left px-5 py-3 font-semibold">Temple</th>
                <th className="text-left px-5 py-3 font-semibold">Type</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-right px-5 py-3 font-semibold">Rows</th>
                <th className="text-right px-5 py-3 font-semibold">Matched</th>
                <th className="text-right px-5 py-3 font-semibold">Merge Ready</th>
                <th className="text-right px-5 py-3 font-semibold">Merged</th>
                <th className="text-right px-5 py-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {jobs.length === 0 ? (
                <tr><td className="px-5 py-6 text-gray-500" colSpan={9}>No jobs yet.</td></tr>
              ) : jobs.map((j) => (
                <tr key={j._id} className="hover:bg-gray-50">
                  <td className="px-5 py-3 text-gray-900">{j.originalFileName}</td>
                  <td className="px-5 py-3 text-gray-900">{j.temple?.name || '-'}</td>
                  <td className="px-5 py-3 text-gray-700">{j.datasetType}</td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-[color-mix(in_srgb,var(--color-primary)_10%,white)] text-[var(--color-primary)]">
                      {j.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">{j.totals?.rows ?? 0}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{j.totals?.matchedUsers ?? 0}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{j.totals?.mergeReady ?? 0}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{j.totals?.merged ?? 0}</td>
                  <td className="px-5 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => handleDeleteJob(j._id)}
                      disabled={loading}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-red-200 bg-white hover:bg-red-50 text-xs font-semibold text-red-700 disabled:opacity-60"
                      title="Delete this import job"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LegacyDataImportPage;

